import { polygon, polygonAmoy, sepolia } from "@reown/appkit/networks";

const defaultContracts = {
  // Sepolia testnet DAI (mock). Production overrides via REACT_APP_DAI_TOKEN
  // — every .env.* in this repo sets it explicitly. Default kept aligned
  // to Sepolia so a developer running without env still sees a wired-up app.
  daiToken: "0x9eC8eF990f925c57B43801fA93739C6CBa720E18",
  frdmOracle: "0xAE503eB503FC7bFf6f0D17298e8CB14Db53808ab",
  frdmRouterPart1Impl: "0x09c8132a315f20B0BD507CA5184917e6A350b274",
  frdmRouterPart2Impl: "0xF01A1bFC28782685dfE8e3CDd95C8838CD4F92E3",
  uniswapV3Router: "0xad473671Ca5A0e8bD08E181D1B323fc3555573f0",
  wfrdmEscrow: "0x27c400A0614b88ba7431CFB71Cc4bfbf2e4634B1",
  frdmNativeToken: "0xADFa72431FA7eab16b5d9243143b5050DE8cD1f1",
  // V2 factory (UUPS-upgradeable token clones via ERC1967Proxy) — Sepolia.
  // Deployed alongside hook v6.3 (see docs/DEPLOY-V2-FRESH-DEVNET.md). The
  // legacy V1 factory still exists on-chain at 0xbF9C…8D4d (Amoy default)
  // and 0x7964…c7ac (Sepolia) but resolves "W<symbol>" to stale clones from
  // pre-V2 deploys. Everything live (server PolygonService, FrdmRouter,
  // currencyRepo.tokenAddress) targets V2; Amoy/.env.develop overrides
  // this default until V2 ships on that chain.
  wfrdmTokenFactory: "0xCE0c3D2A6Bf627dbAde45488552b6F2d32889537",
  frdmRouter: "0x1A25F98e9D3C4B3a7eF2e26BaD421362BdCCa986",
  // Uniswap v4 — Sepolia.
  v4PoolManager: "0xE03A1074c86CFeDd5C142C4F04F1a1536e203543",
  v4PoolSwapTest: "0x9b6b46e2c869aa39918db7f52f5557fe577b6eee",
  v4PoolModifyLiquidityTest: "0x0c478023803a644c94c4ce1c1e7b9a087e411b0a",
  v4StateView: "0xe1dd9c3fa50edb962e442f60dfbc432e24537e4c",
  // Uniswap v4 PositionManager (Sepolia) — production LP path. Mints
  // ERC-721 NFT receipts in the user's wallet, unlike PoolModifyLiquidityTest
  // which is a test harness with no receipt.
  v4PositionManager: "0x429ba70129df741B2Ca2a85BC3A2a3328e5c09b4",
  // Permit2 — Uniswap's canonical Permit2 deployment, used by PositionManager
  // to pull ERC20 approvals. Same address on every chain via CREATE2.
  permit2: "0x000000000022D473030F116dDEE9F6B43aC78BA3",
  // UniversalRouter — Uniswap's canonical, audited v4 swap entrypoint. This
  // is the PRODUCTION swap path (replaces the Sepolia-only PoolSwapTest test
  // router). Deployed on every chain we run on; default below is Sepolia.
  // Polygon mainnet overrides via REACT_APP_V4_UNIVERSAL_ROUTER.
  v4UniversalRouter: "0x3A9D48AB9751398BbFa63ad67599Bb04e4BdF98b",
  // FrdmV4LiquidityHook (V8/V9) — the live LP-backed v4 stack: the hook, its
  // inflation oracle, and the LP share-token implementation. UUPS proxies, so
  // these addresses are stable across implementation upgrades.
  frdmV4LiquidityHook: "0xA5F9e591F0C06ACcfA576d14f73Fa22C551cdA88",
  frdmInflationOracle: "0xC7F5E17c24D5Db462B3509AD66a0ed25E0FE705D",
  frdmShareTokenImpl: "0xD469C147Be8cA0f465821dd5fDEcDB98e6f9d84E",
};

export const CONTRACTS = {
  daiToken: process.env.REACT_APP_DAI_TOKEN || defaultContracts.daiToken,
  frdmOracle: process.env.REACT_APP_FRDM_ORACLE || defaultContracts.frdmOracle,
  frdmRouterPart1Impl:
    process.env.REACT_APP_FRDM_ROUTER_PART1_IMPL ||
    defaultContracts.frdmRouterPart1Impl,
  frdmRouterPart2Impl:
    process.env.REACT_APP_FRDM_ROUTER_PART2_IMPL ||
    defaultContracts.frdmRouterPart2Impl,
  uniswapV3Router:
    process.env.REACT_APP_UNISWAP_V3_ROUTER || defaultContracts.uniswapV3Router,
  wfrdmEscrow:
    process.env.REACT_APP_WFRDM_ESCROW || defaultContracts.wfrdmEscrow,
  frdmNativeToken:
    process.env.REACT_APP_FRDM_NATIVE_TOKEN || defaultContracts.frdmNativeToken,
  wfrdmTokenFactory:
    process.env.REACT_APP_WFRDM_TOKEN_FACTORY ||
    defaultContracts.wfrdmTokenFactory,
  frdmRouter: process.env.REACT_APP_FRDM_ROUTER || defaultContracts.frdmRouter,
  v4PoolManager:
    process.env.REACT_APP_V4_POOL_MANAGER || defaultContracts.v4PoolManager,
  v4PoolSwapTest:
    process.env.REACT_APP_V4_POOL_SWAP_TEST || defaultContracts.v4PoolSwapTest,
  v4PoolModifyLiquidityTest:
    process.env.REACT_APP_V4_POOL_MODIFY_LIQUIDITY_TEST ||
    defaultContracts.v4PoolModifyLiquidityTest,
  v4StateView:
    process.env.REACT_APP_V4_STATE_VIEW || defaultContracts.v4StateView,
  v4PositionManager:
    process.env.REACT_APP_V4_POSITION_MANAGER ||
    defaultContracts.v4PositionManager,
  permit2: process.env.REACT_APP_PERMIT2 || defaultContracts.permit2,
  v4UniversalRouter:
    process.env.REACT_APP_V4_UNIVERSAL_ROUTER ||
    defaultContracts.v4UniversalRouter,
  frdmV4LiquidityHook:
    process.env.REACT_APP_FRDM_V4_LIQUIDITY_HOOK ||
    defaultContracts.frdmV4LiquidityHook,
  frdmInflationOracle:
    process.env.REACT_APP_FRDM_INFLATION_ORACLE ||
    defaultContracts.frdmInflationOracle,
  frdmShareTokenImpl:
    process.env.REACT_APP_FRDM_SHARE_TOKEN_IMPL ||
    defaultContracts.frdmShareTokenImpl,
};

/* ─────────────────────────── which chain ───────────────────────────
 * Two independent env vars used to answer this, read by different files:
 *   REACT_APP_CHAIN     (a name)   → lib/wagmi.js, to pick the network
 *   REACT_APP_CHAIN_ID  (a number) → CHAIN_ID below, and ~12 read hooks
 *
 * Nothing checked they agreed. A mismatched pair is not a small bug: wagmi
 * connects on one chain while every hook gated on `chainId === CHAIN_ID`
 * silently returns nothing and every signature fails inside wagmi with a
 * message naming neither chain — the mobile login failure, but permanent and
 * for all users. So resolve both from one input and refuse to load if they
 * disagree. A build that cannot say which chain it is on must not run.
 */

/** Every chain this codebase knows how to be deployed on, keyed by env name. */
const SUPPORTED_CHAINS = { sepolia, polygon, polygonAmoy };

/** Preserves the historical fallback of the old `CHAIN_ID` default. */
const DEFAULT_CHAIN_NAME = "polygonAmoy";

/**
 * REPORTS the problem, does not throw.
 *
 * Throwing here looked right and was wrong: this module is evaluated while
 * index.js's import graph is still loading, so the throw lands BEFORE
 * `root.render()`. The ErrorBoundary never mounts, `installGlobalErrorHandlers`
 * has not run, and production builds stub `console.log` — the result is a blank
 * white page with no diagnostic anywhere. Verified in a real build.
 *
 * So: resolve to something usable, hand the message to index.js, and let the
 * entry point refuse to mount the app and render the reason instead.
 *
 * @returns {{ network: object, error: string|null }}
 */
function resolveChain() {
  const name = (process.env.REACT_APP_CHAIN || DEFAULT_CHAIN_NAME).trim();
  const network = SUPPORTED_CHAINS[name];

  if (!network) {
    return {
      network: SUPPORTED_CHAINS[DEFAULT_CHAIN_NAME],
      error:
        `REACT_APP_CHAIN="${name}" is not a chain this build supports. ` +
        `Expected one of: ${Object.keys(SUPPORTED_CHAINS).join(", ")}.`,
    };
  }

  const declaredId = process.env.REACT_APP_CHAIN_ID;
  // Absent is fine — the network object is authoritative. Present-and-wrong is
  // the dangerous case, and the only one worth refusing to boot over.
  if (declaredId !== undefined && declaredId !== "") {
    const parsed = Number.parseInt(declaredId, 10);
    if (!Number.isFinite(parsed)) {
      return { network, error: `REACT_APP_CHAIN_ID="${declaredId}" is not a number.` };
    }
    if (parsed !== network.id) {
      return {
        network,
        error:
          `Chain config disagrees with itself: REACT_APP_CHAIN="${name}" is chain ` +
          `${network.id}, but REACT_APP_CHAIN_ID="${declaredId}". Fix the .env for ` +
          `this environment — every signature and every balance read would fail ` +
          `with this pair.`,
      };
    }
  }

  return { network, error: null };
}

const resolved = resolveChain();

/**
 * Non-null when this build's chain config contradicts itself. index.js renders
 * this instead of mounting the app — a build that cannot say which chain it is
 * on must not run, but it must say so out loud rather than showing a blank page.
 */
export const CHAIN_CONFIG_ERROR = resolved.error;

/** The AppKit network object. `modal.switchNetwork()` accepts nothing else. */
export const APP_NETWORK = resolved.network;

/** The numeric chain id. Unchanged name — ~12 hooks already import it from here. */
export const CHAIN_ID = APP_NETWORK.id;

/** Human-readable, for user-facing copy ("Switch to Polygon"). */
export const APP_CHAIN_NAME = APP_NETWORK.name;

/** CAIP-2 id, the form AppKit persists in localStorage. */
export const APP_CAIP_NETWORK_ID = `eip155:${CHAIN_ID}`;

/**
 * Read-only RPC endpoints, in the order they are tried. Comma-separated in
 * REACT_APP_RPC_URLS. Free public endpoints only: everything here ships in the
 * browser bundle, so no paid or keyed URL may ever go in this list.
 *
 * REACT_APP_RPC_URL (the old single-URL name) is still read when the new one
 * is unset, so a build whose Amplify ENV_FILE has not been updated keeps a
 * working endpoint instead of silently dropping back to AppKit's default.
 */
const parseRpcUrls = (raw) =>
  (raw || "")
    .split(",")
    .map((url) => url.trim())
    .filter(Boolean);

export const RPC_URLS = parseRpcUrls(
  process.env.REACT_APP_RPC_URLS || process.env.REACT_APP_RPC_URL
);

if (!process.env.REACT_APP_RPC_URLS && process.env.REACT_APP_RPC_URL) {
  console.warn(
    "REACT_APP_RPC_URL is deprecated — set REACT_APP_RPC_URLS (comma-separated) instead."
  );
}

/** First endpoint, for callers that need a single URL. */
export const RPC_URL = RPC_URLS[0];

/* global BigInt */
export const GAS_MULTIPLIER = process.env.REACT_APP_GAS_MULTIPLIER
  ? BigInt(process.env.REACT_APP_GAS_MULTIPLIER)
  : 5n;

/**
 * Upper bound on the gas limit we will ask a wallet to send.
 *
 * Infura rejects any transaction above 16,777,216 gas. `openDispute` estimates
 * around 4.2M, and 4.2M x GAS_MULTIPLIER(5) is 21,000,000 — over the cap. The
 * result was that whether a dispute could be opened at all depended on which
 * RPC the user's wallet happened to be using, with an error naming neither the
 * cap nor the cause.
 *
 * Default sits just under Infura's limit. Chains with a higher block gas limit
 * can raise it via env.
 */
export const MAX_GAS_LIMIT = process.env.REACT_APP_MAX_GAS_LIMIT
  ? BigInt(process.env.REACT_APP_MAX_GAS_LIMIT)
  : 16000000n;

/**
 * Apply the safety multiplier to a gas estimate, then clamp it.
 *
 * Returns undefined for a missing estimate so callers can spread the result
 * (`...(gas ? { gas } : {})`) and let the wallet estimate for itself, which is
 * what every call site already does.
 */
export function boundedGas(estimate, multiplier = GAS_MULTIPLIER) {
  if (estimate === undefined || estimate === null) return undefined;
  try {
    const scaled = BigInt(estimate) * BigInt(multiplier);
    return scaled > MAX_GAS_LIMIT ? MAX_GAS_LIMIT : scaled;
  } catch (err) {
    return undefined;
  }
}
